import type { CSSProperties } from 'react';
import { useMemo } from 'react';

import classNames from 'classnames';

import { PictureElement } from '@/pictureElement/Picture';
import { computeColor } from '@/utils/colors';
import { computeFont } from '@/utils/fonts';
import { getFontSizeVariables } from '@/utils/getFontSizeVariables';
import { rewriteImageSrc } from '@/utils/rewriteImageSrc';

import { getHeightStyle, getWidthStyle } from '../../../utils/sizing';

import Image from '../../../icon/Image';
import * as styles from '../../Navbar.css';
import {
  NAVBAR_MENU_ITEM_BORDER_DEFAULTS,
  NAVBAR_MENU_ITEM_LINK_DEFAULTS_V2,
  NAVBAR_MENU_ITEM_LOGO_DEFAULTS_V2,
} from '../NavbarMenuItem.constants';
import type { NavbarMenuItemV2ElementProps } from '../NavbarMenuItem.type';

export function NavbarMenuItemLogoV2(props: NavbarMenuItemV2ElementProps) {
  const { element, className, children, onClick, attributes } = props;

  if (element.attrs?.type !== 'logo') {
    return null;
  }

  const {
    customAttributes,
    href,
    new_tab,
    isCustomLink,
    src,
    width,
    height,
    padding,
    borderRadius,
    background,
    borderColor,
    borderWidth,
    borderStyle,
    hoverBorderColor,
    hoverBorderStyle,
    hoverBorderWidth,
    hoverBorderRadius,
    hoverBackgroundColor,
  } = element.attrs || {};

  const { color, fontFamily, fontSize, fontWeight, mobileFontSize, mobileColor, mobilePadding } = element.attrs || {};

  const Comp = href ? 'a' : 'div';

  const cleanHref = useMemo(() => {
    try {
      const hrefURL = href ? new URL(href) : undefined;
      if (href && hrefURL && !isCustomLink) {
        return href.replace(hrefURL.origin, '');
      }
    } catch (_e) {}
    return href;
  }, [href, isCustomLink]);

  const finalWidth = width ? getWidthStyle(width).width : NAVBAR_MENU_ITEM_LOGO_DEFAULTS_V2.width;
  const finalMaxWidth = width ? getWidthStyle(width).maxWidth : NAVBAR_MENU_ITEM_LOGO_DEFAULTS_V2.width;
  const finalHeight = height ? getHeightStyle(height).height : NAVBAR_MENU_ITEM_LOGO_DEFAULTS_V2.height;

  const imgSrc = rewriteImageSrc(src);
  const image = src ? (
    <PictureElement src={imgSrc}>
      <img src={imgSrc} alt="Logo" className={styles.navbarItemLogoImageV2} />
    </PictureElement>
  ) : (
    <Image size={typeof height === 'number' ? height : 36} fill={'#a3a3a3'} />
  );

  return (
    <div
      {...customAttributes}
      className={classNames(styles.navbarItemLinkContainerV2)}
      {...attributes}
      style={
        {
          '--text-color': computeColor(color ?? NAVBAR_MENU_ITEM_LINK_DEFAULTS_V2.color),
          '--font-family': computeFont(fontFamily) || NAVBAR_MENU_ITEM_LINK_DEFAULTS_V2.fontFamily,
          '--font-weight': fontWeight ?? NAVBAR_MENU_ITEM_LINK_DEFAULTS_V2.fontWeight,
          ...getFontSizeVariables(fontSize ?? NAVBAR_MENU_ITEM_LINK_DEFAULTS_V2.fontSize, mobileFontSize),
          '--mobile-text-color': computeColor(mobileColor || color || NAVBAR_MENU_ITEM_LINK_DEFAULTS_V2.color),
          '--mobile-padding': mobilePadding || padding || NAVBAR_MENU_ITEM_LINK_DEFAULTS_V2.padding,
        } as CSSProperties
      }
    >
      <Comp
        {...props}
        onClick={(e) => {
          if (onClick) {
            e?.preventDefault();
            onClick();
          }
        }}
        role="presentation"
        className={classNames(styles.navbarItemLogoV2, className, customAttributes?.className)}
        style={
          {
            '--width': finalWidth === 'fit-content' ? 'auto' : finalWidth, // fit-content for img defaults to fill on safari
            '--height': finalHeight === 'fit-content' ? 'auto' : finalHeight,
            '--max-width': finalMaxWidth === 'fit-content' ? 'auto' : finalMaxWidth,
            '--min-width': 'min(36px, var(--height))',
            '--padding': padding ?? NAVBAR_MENU_ITEM_LOGO_DEFAULTS_V2.padding,
            '--border-radius': borderRadius ?? NAVBAR_MENU_ITEM_BORDER_DEFAULTS.borderRadius,

            '--hover-border-radius': hoverBorderRadius || borderRadius || NAVBAR_MENU_ITEM_BORDER_DEFAULTS.borderRadius,
            '--hover-background-color':
              hoverBackgroundColor || background || NAVBAR_MENU_ITEM_LOGO_DEFAULTS_V2.background,
            '--hover-border-color': hoverBorderColor || borderColor || NAVBAR_MENU_ITEM_BORDER_DEFAULTS.borderColor,
            '--hover-border-style': hoverBorderStyle || borderStyle || NAVBAR_MENU_ITEM_BORDER_DEFAULTS.borderStyle,
            '--hover-border-width': hoverBorderWidth || borderWidth || NAVBAR_MENU_ITEM_BORDER_DEFAULTS.borderWidth,
          } as CSSProperties
        }
        href={Comp === 'a' ? cleanHref : undefined}
        target={Comp === 'a' ? (new_tab ? '_blank' : '_self') : undefined}
      >
        {image}
      </Comp>
      <span className={classNames(styles.navbarItemLogoSeparatorParentV2)}>{children}</span>
    </div>
  );
}
